import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment, { now } from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import _, { isEqual, uniqBy } from 'lodash';

import { PageHeader } from '../../../components/PageHeader';
import DashboardEditModeButton from '../../Dashboards/DashboardEditModeButton';
import DashboardFilter from '../../Dashboards/DashboardFilter';
import { Button } from '../../../components/PageHeader/Button';
import DashboardLayout from '../../Dashboards/DashboardLayout';
import Strings from '../../../Strings';
import { actions } from './redux/actions';
import { actions as orgActions } from '../../Organizations/redux/actions';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../../components/OrgSelect/orgFilterActions';
import { getDefaultPeriods } from './Report';
import { ColorPalette } from '../../Dashboards/DashboardColorPalettes';
import { actionsAdvancedFilters } from '../../../components/AdvancedFilters/actions';
import { openModalAction } from '../../../actions/modal';
import { isSuperUser } from '../../../utils/userRoles';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import {
  CareProviderFilter,
  getFiltersDefinition,
  OrgFilter,
  OrgGroupFilter,
} from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { DASHBOARD_ID, getDefaultLayouts } from './reportWigetsMetadata';
import initializeDashboard from './reportWigets';
import { DateFilters } from '../../../utils/DateFilters';
import buildReportRequest from './reportRequest';
import { programsAndSubprograms } from '../../../utils/cmsPrograms';

const REMOVED_REPORT_FILTER_GROUPS = ['org_administrative_state', 'org_business_state', 'org_operational_state'];

function ReportDashboard(props) {
  const {
    downloadActions,
    dashboardSettingsFeature,
    colorPalette,
    configuration,
    advancedFilters,
    getOrgsForFilter,
    getProgramStatistics,
    getEnrollmentsMetrics,
    configDashboard,
    getOrganizationGroups,
    getCareProviders,
    updateAdvancedFilterState,
  } = props;

  const dataReducer = (state, action) => {
    switch (action.type) {
      case 'dataFetched':
        return { ...state, loaded: true, data: [...state.data, ...action.data] };
      case 'startDataFetching':
        return { ...state, loaded: false, data: [] };
      default:
        throw new Error('Unexpected action');
    }
  };

  const [widgets, setWidgets] = useState([]);
  const [refreshTimestamp, setRefreshTimestamp] = useState(now());
  const [widgetResizeTimestamps, setWidgetResizeTimestamps] = useState({});
  const [refreshEnable, setRefreshEnable] = useState(true);

  const [orgsForFilter, setOrgsForFilter] = useReducer((state, newState) => ({ ...state, ...newState }), { loaded: false, data: [] });
  const [orgGroups, setOrgGroups] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const [data, setData] = useReducer(dataReducer, { loaded: false, data: [] });
  const [data30days, setData30days] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });
  const [enrollmentMetrics, setEnrollmentMetrics] = useReducer((state, newState) => ({ ...state, ...newState }), {
    loaded: false,
    data: {},
  });

  const periods = getDefaultPeriods();
  const request = React.useRef();
  const requestSequence = React.useRef(0);

  useEffect(() => {
    advancedFilters
      .filter(filter => REMOVED_REPORT_FILTER_GROUPS.includes(filter.group) && filter.state)
      .forEach(filter => {
        updateAdvancedFilterState(
          {
            group: filter.group,
            value: filter.value,
            state: false,
          },
          undefined,
        );
      });
  }, [advancedFilters, updateAdvancedFilterState]);

  useEffect(() => {
    initializeDashboard(
      data,
      data30days,
      enrollmentMetrics,
      setWidgets,
      configuration,
      colorPalette,
      dashboardSettingsFeature,
      refreshTimestamp,
      widgetResizeTimestamps,
    );
  }, [
    data,
    data30days,
    colorPalette,
    configuration,
    refreshTimestamp,
    enrollmentMetrics,
    widgetResizeTimestamps,
  ]);

  useEffect(() => {
    getOrgsForFilter().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgsForFilter({
          loaded: true,
          data: resp.response?.data?.map(o => ({ ...o, value: o.masked_id, label: o.name })),
        });
      }
    });
    getOrganizationGroups().then(resp => {
      setOrgGroups(resp?.response?.data || []);
    });
    getCareProviders().then(resp => {
      setCareProviders(resp?.response ? uniqBy(resp.response, 'uuid') : []);
    });
  }, [refreshTimestamp]);

  React.useEffect(() => {
    const reportFilters = advancedFilters.filter(filter => !REMOVED_REPORT_FILTER_GROUPS.includes(filter.group));
    const filterRequest = buildReportRequest(reportFilters, programsAndSubprograms);
    const advFilters = getRelevantFilters(reportFilters);
    const dateFilter = advFilters.find(f => f.group === 'dateFilter')?.value;
    const dates = DateFilters[dateFilter];

    const startTimestamp =
      dates?.dates?.startDate() ||
      moment()
        .subtract(30, 'days')
        .startOf('day')
        .format();
    const endTimestamp =
      dates?.dates?.endDate() ||
      moment()
        .subtract(1, 'days')
        .endOf('day')
        .format();

    const requestSnapshot = { filterRequest, dateFilter };
    if (isEqual(requestSnapshot, request.current)) {
      return;
    }
    request.current = requestSnapshot;
    const sequence = requestSequence.current + 1;
    requestSequence.current = sequence;

    setData({ type: 'startDataFetching' });
    setData30days({ loaded: false, data: {} });
    setEnrollmentMetrics({ loaded: false, data: {} });

    // Fetch data sequentially to avoid exhausting connection pool
    // This prevents 8+ concurrent API calls that were blocking the connection pool
    const fetchDataSequentially = async () => {
      // Import cache utility dynamically
      const { cachedDispatch } = await import('../../../utils/apiCache');

      // Fetch current period data (30 days) with caching
      const currentResp = await cachedDispatch(
        'program_statistics',
        { ...filterRequest, startTimestamp, endTimestamp },
        () => getProgramStatistics({ ...filterRequest, startTimestamp, endTimestamp }),
        60000, // Cache for 1 minute
      );
      if (requestSequence.current !== sequence) return;
      setData30days({ loaded: true, data: currentResp.response });

      // Fetch enrollment metrics in parallel with historical data (different endpoint)
      cachedDispatch('enrollments_metrics', filterRequest, () => getEnrollmentsMetrics(filterRequest), 60000).then(
        resp => {
          if (requestSequence.current !== sequence) return;
          setEnrollmentMetrics({ loaded: true, data: resp.response });
        },
      );

      // Fetch historical periods sequentially to avoid connection pool exhaustion
      for (const period of periods) {
        const resp = await cachedDispatch(
          'program_statistics',
          {
            ...filterRequest,
            startTimestamp: period.start,
            endTimestamp: period.end,
          },
          () =>
            getProgramStatistics({
              ...filterRequest,
              startTimestamp: period.start,
              endTimestamp: period.end,
            }),
          60000,
        );
        if (requestSequence.current !== sequence) return;
        const monthData = resp.response;
        setData({
          type: 'dataFetched',
          data: [
            {
              periodNumber: Math.ceil(moment(period.start).diff(moment(), 'months', true)),
              patientsPerProgram: {
                rtmResp: monthData.RTM?.total_patients_per_subprogram?.RTMRespiratory || 0,
                rtmMs: monthData.RTM?.total_patients_per_subprogram?.RTMMusculoskeletal || 0,
                rtmBeh: monthData.RTM?.total_patients_per_subprogram?.RTMBehavioralHealth || 0,
                rpm: monthData.RPM?.total_patients_per_subprogram?.RPM || 0,
                pcm: monthData.PCM?.total_patients_per_subprogram?.PCM || 0,
                ccmBasic: monthData.CCM?.total_patients_per_subprogram?.CCMBasic || 0,
                ccmComplex: monthData.CCM?.total_patients_per_subprogram?.CCMComplex || 0,
              },
              adherence: Array.from({ length: 11 }, (_, i) => i * 10).reduce((acc, curr) => {
                acc[curr.toString()] =
                  (monthData.RTM?.adherence_distribution?.[curr] || 0) +
                  (monthData.RPM?.adherence_distribution?.[curr] || 0) +
                  (monthData.CCM?.adherence_distribution?.[curr] || 0) +
                  (monthData.PCM?.adherence_distribution?.[curr] || 0);
                return acc;
              }, {}),
            },
          ],
        });
      }
    };

    fetchDataSequentially();
  }, [advancedFilters]);

  const onRefresh = () => {
    setRefreshEnable(false);
    setTimeout(() => {
      setRefreshEnable(true);
    }, 10000);
    setRefreshTimestamp(now());
  };

  const layouts = getDefaultLayouts({
    skipPermissionCheck: isSuperUser(),
  });

  const onResize = _.debounce((layouts, oldItem, newItem) => {
    if (configuration?.[newItem.i]?.chartType?.value === 'table') {
      setWidgetResizeTimestamps(prev => ({ ...prev, [newItem.i]: moment() }));
    }
  }, 30);

  return (
    <>
      <PageHeader
        isBlack
        left={Strings.report}
        right={
          <>
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition().programAndSubprogram} />
              {orgsForFilter.data?.length > 0 && <Filter definition={OrgFilter(orgsForFilter.data).orgId} />}
              {orgGroups?.length > 0 && <Filter definition={OrgGroupFilter(orgGroups).orgGroup} />}
              {careProviders?.length > 0 && <Filter definition={CareProviderFilter(careProviders).provider} />}
            </AdvancedFilters>
            <DashboardEditModeButton dashboardId={DASHBOARD_ID} />
            {dashboardSettingsFeature && (
              <div className="settings-button">
                <Button class="settings-button-icon" onClick={configDashboard}></Button>
              </div>
            )}
            <DashboardFilter
              dashboardId={DASHBOARD_ID}
              additionalData={{
                skipPermissionCheck: isSuperUser(),
              }}
            />
            {downloadActions}
            <Button class="refresh" disabled={!refreshEnable} onClick={onRefresh} />
          </>
        }
      />
      <AdvancedFiltersBar
        customFilters={{ ...OrgFilter(orgsForFilter.data), ...OrgGroupFilter(orgGroups), ...CareProviderFilter(careProviders) }}
      />
      {widgets.length > 0 && !_.isEmpty(layouts) && (
        <OverlayScrollbarsComponent
          defer
          className="scrollbar-right-margin with-bottom-margin"
          options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        >
          <DashboardLayout
            dashboardId={DASHBOARD_ID}
            widgets={widgets}
            defaultLayouts={layouts}
            additionalData={{ skipPermissionCheck: isSuperUser() }}
            rowHeight={45}
            breakpoints={{ l: 1500, m: 1200, s: 800 }}
            onResize={onResize}
          />
        </OverlayScrollbarsComponent>
      )}
    </>
  );
}

ReportDashboard.propTypes = {
  userName: PropTypes.string,
  filters: PropTypes.array,
  configuration: PropTypes.any,
  initializeDashboard: PropTypes.func,
  getOrgsForFilter: PropTypes.func.isRequired,
  getOrganizationGroups: PropTypes.func.isRequired,
  getCareProviders: PropTypes.func.isRequired,
  getProgramStatistics: PropTypes.func.isRequired,
  downloadActions: PropTypes.node,
  dashboardSettingsFeature: PropTypes.bool,
  colorPalette: PropTypes.object,
  advancedFilters: PropTypes.array,
  updateAdvancedFilterState: PropTypes.func.isRequired,
  configDashboard: PropTypes.func,
};

const mapStateToProps = state => {
  const { profile } = state.auth;
  const { dashboardFilters } = state.dashboardState;
  const filters = dashboardFilters[DASHBOARD_ID];
  const configuration = state.dashboardState.configuration[DASHBOARD_ID];
  const labFeatures = state.labState.features;
  return {
    userName: profile ? (profile.first_name ? `${profile.first_name} ${profile.last_name}` : profile.username) : '',
    filters,
    configuration,
    colorPalette:
      ColorPalette[state.dashboardState.configuration?.allDashboards?.colorPalette] || Object.values(ColorPalette)[0],
    dashboardSettingsFeature: labFeatures.find(f => f.name === 'Dashboard configuration')?.selected,
    advancedFilters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  initializeDashboard: (dashboardId, defaultLayouts, widgets, force) =>
    dispatch(actions.initializeDashboard(dashboardId, defaultLayouts, widgets, force)),
  getProgramStatistics: request => dispatch(actions.getProgramStatistics(request)),
  getEnrollmentsMetrics: request => dispatch(actions.getEnrollmentsMetrics(request)),
  getOrgsForFilter: request => dispatch(orgFilterActions.getMyOrgs(request)),
  getOrganizationGroups: pageRequest => dispatch(orgActions.getOrganizationGroups(pageRequest)),
  getCareProviders: request => dispatch(orgActions.getCareProviders(request)),
  updateAdvancedFilterState: (data, filterType) =>
    dispatch(actionsAdvancedFilters.updateFilterState(data, filterType)),
  configDashboard: () =>
    dispatch(openModalAction('dashboard-configuration', { dashboardId: DASHBOARD_ID, additionalData: true })),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportDashboard);
